import _, { isArray } from 'lodash';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { exportModule } from '../services/Export';
import { handleApiError, showSuccessToast } from './errorHandler';

export const getDeletedValues = (oldObj, newObj) => {
  const deletedValues = {};

  // Iterate through old object keys
  Object.keys(oldObj).forEach(key => {
    // Check if key exists in new object
    if (!_.has(newObj, key)) {
      // If key is completely removed
      deletedValues[key] = oldObj[key];

    } else {
      // For array-based fields
      if (Array.isArray(oldObj[key]) && Array.isArray(newObj[key])) {

        // Check if the array is a single entry with comma-separated values
        const isSingleCommaString = oldObj[key].length === 1 && 
        typeof oldObj[key][0] === 'string' && oldObj[key][0].includes(',');

        // Skip comparison if it's a single comma-separated string array
        if (isSingleCommaString) {
          return;
        }

        // Find array elements in old object that are not in new object
        const deletedArrayItems = oldObj[key].filter(oldItem => 
          !newObj[key].some(newItem => _.isEqual(oldItem, newItem))
        );

        // If any items are deleted, add to result
        if (deletedArrayItems.length > 0) {
          deletedValues[key] = deletedArrayItems;
        }
      }
      // For nested objects
      else if (_.isPlainObject(oldObj[key]) && _.isPlainObject(newObj[key])) {
        const nestedDeleted = getDeletedValues(oldObj[key], newObj[key]);
        if (!_.isEmpty(nestedDeleted)) {
          deletedValues[key] = nestedDeleted;
        }
      }
    }
  });

  return deletedValues;
};

export const getRemovedValues = (existingData, currentData) => {
  const currentIds = currentData.map(item => item._id); // Extract `_id`s from current data
  return existingData
    ?.filter(item => !currentIds.includes(item._id)) // Find items not in current data
    .map(item => ({ _id: item._id })); // Map `_id` into an object with key `_id`
};

export function removeKeysFromFormData(formData, keysToRemove) {
  if (!formData || typeof formData !== 'object') {
    throw new Error("formData must be an object.");
  }

  if (!Array.isArray(keysToRemove)) {
    throw new Error("keysToRemove must be an array.");
  }

  return Object.keys(formData).reduce((result, key) => {
    if (!keysToRemove.includes(key)) {
      result[key] = formData[key];
    }
    return result;
  }, {});
}

export const transformData = (data, fieldsToTransform) => {
  // First, handle all null values in the entire data object
  const handleNullValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => handleNullValues(item));
    } else if (obj && typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (obj[key] === null || obj[key] === undefined) {
          result[key] = '';
        } else {
          result[key] = handleNullValues(obj[key]);
        }
      }
      return result;
    }
    return obj;
  };

  // Handle null values in the entire data structure
  const dataWithNullsHandled = handleNullValues(data);

  // Create a copy of the null-handled data for transformation
  const transformedData = { ...dataWithNullsHandled };

  // Iterate through the fields to transform
  fieldsToTransform.forEach(field => {
    // Check if the field exists in the data and handle null values
    if (dataWithNullsHandled[field] !== undefined && dataWithNullsHandled[field] !== null && Array.isArray(dataWithNullsHandled[field])) {
      // Transform the field
      transformedData[field] = dataWithNullsHandled[field].flatMap(item => {
        // Handle both string and object formats
        if (typeof item === 'string') {
          // If it's a string with commas, split it
          return item.includes(',') 
            ? item.split(',').map(subItem => ({
                image_alias: subItem.trim(),
                image: subItem.trim()
              }))
            : [{ 
                image_alias: item.trim(), 
                image: item.trim() 
              }];
        } else if (typeof item === 'object' && item !== null) {
          // If it's already an object, ensure it has image_alias and image
          return [{
            image_alias: item.image_alias || item.image || '',
            image: item.image || item.image_alias || ''
          }];
        }
        
        // Return empty array for unhandled types
        return [];
      });
    } else if (dataWithNullsHandled[field] === null || dataWithNullsHandled[field] === undefined) {
      // Convert null or undefined values to empty string
      transformedData[field] = '';
    }
  });

  return transformedData;
};


// export const reverseTransformData = (data, fieldsToReverse) => {
//   // Create a copy of the original data
//   const reversedData = { ...data };

//   // Iterate through the fields to reverse
//   fieldsToReverse.forEach(field => {
//     // Check if the field exists in the data
//     if (data[field] !== undefined && Array.isArray(data[field])) {

//       console.log("data", data);
//       // Reverse transform the field
//       reversedData[field] = data[field].length > 1
//         ? [data[field].map(item => item.image_alias || item.image).join(',')]
//         : data[field];
//     }
//   });

//   return reversedData;
// };

export const reverseTransformData = (data, fieldsToReverse) => {
  // Input validation
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('The "data" parameter must be a valid object.');
  }

  if (!Array.isArray(fieldsToReverse)) {
    throw new Error('The "fieldsToReverse" parameter must be an array.');
  }

  // First, handle all null values in the entire data object
  const handleNullValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => handleNullValues(item));
    } else if (obj && typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (obj[key] === null || obj[key] === undefined) {
          result[key] = '';
        } else {
          result[key] = handleNullValues(obj[key]);
        }
      }
      return result;
    }
    return obj;
  };

  // Handle null values in the entire data structure
  const dataWithNullsHandled = handleNullValues(data);

  // Create a shallow copy of the null-handled data
  const reversedData = { ...dataWithNullsHandled };

  // Process each field in the fieldsToReverse array
  fieldsToReverse.forEach(field => {
    const fieldData = dataWithNullsHandled[field];
    // Handle null or undefined values by converting to empty string
    if (fieldData === null || fieldData === undefined) {
      reversedData[field] = '';
      return;
    }

    // Skip if the field is not an array
    if (!Array.isArray(fieldData)) {
      return;
    }

    // Ensure the fieldData array is non-empty before transforming
    if (fieldData.length > 0) {
      // Use Set to remove duplicates and map items to image_alias or image
      const uniqueImages = Array.from(
        new Set(
          fieldData
            .map(item => item.image_alias || item.image) // Extract image_alias or image
            .filter(Boolean) // Remove falsy values like null/undefined/empty strings
        )
      );

      // Assign the unique images back to the reversedData object
      reversedData[field] = uniqueImages;
    }
  });

  return reversedData;
};

export const showImages = (columnHeaders, openImageModal) => {

  return columnHeaders.map((header) => {
    if (header === "image") {
      return {
        renderer(instance, td, row, col, prop, value, cellProperties) {
          const img = document.createElement("img");
          img.src = value;
          img.style.width = "32px"; // Adjust the width as needed
          img.style.height = "auto";
          img.style.display = "block"; // Ensure the image is displayed as a block element
          // const textNode = document.createTextNode(value);
          img.style.cursor = "pointer";

          img.addEventListener("click", () => {
              openImageModal(value); // Open modal on image click
            });

            td.innerText = "";
            td.appendChild(img);

          td.innerText = "";
          td.appendChild(img);

          return td;
        },
      };
    }
    return {};
  })
  
}


export const createDataObject = (dataArray) => {
  const dataObject = {};
  if (dataArray.length > 0) {
    const dataItem = dataArray[0];
    for (const key in dataItem) {
      dataObject[key] = dataItem[key];
    }
  }
  return dataObject;
};

export const transformOptions = (data, keyForLabelValue = "alias") => {
  return data?.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
      }, {});
  
      // Use the `alias` field for label and value
      return {
      label: entity[keyForLabelValue],
      value: entity[keyForLabelValue],
      };
  });
  };

  export const updateFacetsData = (result) => {
  return result?.data?.facetsHeaders?.map((header) => {
    const key = Object.keys(header)[0];
    const display = header[key];
    const value = isArray( result?.data?.facets) ? result?.data?.facets[0][key] : result?.data?.facets[key]; // Assuming facetsData has only one object
    // Backend caps each facet's returned value list (Algolia's own platform
    // limit) and reports whether more values exist beyond what's shown, plus
    // a ready-to-display "<n>+" string (see capFacetsAndBuildMeta in
    // osw-backend's getAggregateDataAll.js) - carried through here so the
    // accordion header can show "999+" instead of a bare, misleading count.
    const meta = result?.data?.facetsMeta?.[key];

    // Only include facets that have a value
    if (value && value.length > 0) {
      return {
        key,
        display,
        value,
        hasMore: meta?.hasMore || false,
        displayCount: meta?.displayCount,
      };
    } else {
      return null;
    }
  }).filter(facet => facet !== null);
}

export function totalFilterCount(obj) {
  return Object.values(obj).reduce((total, value) => {
    if (Array.isArray(value)) {
      total += value?.length;
    }
    return total;
  }, 0);
}

export const removeFilter = (key, value, setFilterOptions, setFilterPills, setApply,setSearchTerm) => {
  if (key === 'search') {
    setFilterOptions((prevOptions) => ({
      ...prevOptions,
      search: "", // Reset search field
    }));
    setSearchTerm('');
    setFilterPills((prevOptions) => ({
      ...prevOptions,
      search: "", // Reset search field in pills
    }));
  } else {
    // Handle removing a filter from filters object
  setFilterOptions((prevOptions) => {
    const updatedFilters = { ...prevOptions.filters };
    updatedFilters[key] = updatedFilters[key]?.filter(item => item !== value);
    
    return {
      ...prevOptions,
      filters: updatedFilters,
    };
  });
  setFilterPills((prevOptions) => {
    const updatedFilters = { ...prevOptions.filters };
    updatedFilters[key] = updatedFilters[key].filter(item => item !== value);
    
    return {
      ...prevOptions,
      filters: updatedFilters,
    };
  });
}
  setApply(true);
};

/**
 * Cleans HTML tags from a string
 * @param {string} text - The text to clean
 * @returns {string} - The cleaned text without HTML tags
 */
export const cleanText = (text) => {
  if (!text) return '';
  // Remove HTML tags and decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * React component to render HTML string safely
 * @param {object} props - Component props
 * @param {string} props.htmlString - The HTML string to render
 * @returns {JSX.Element} - Rendered HTML content
 */
export const HtmlRenderer = ({ htmlString }) => {
  if (!htmlString) return null;
  return <span dangerouslySetInnerHTML={{ __html: htmlString }} />;
};

/**
 * Custom hook to get URL search parameters
 * @returns {URLSearchParams} - URLSearchParams object
 */
export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * Common export handler function that accepts module name as parameter
 * @param {string} moduleName - The module name to export (e.g., "film", "posts", "personality", "masterlist", etc.)
 * @param {Object} toast - Chakra UI toast instance from useToast()
 * @param {Object|Array} moduleInputs - Optional module inputs object or array (e.g., { type: "Personality", alias: "alias_value" } or [{ type: "Personality", alias: "alias_value" }])
 * @returns {Promise<void>}
 */
export const handleExport = async (moduleName, toast, moduleInputs = {}) => {
  if (!moduleName) {
    handleApiError(
      new Error('Module name is required'),
      toast,
      {
        defaultErrorMessage: 'Export module name is missing'
      }
    );
    return;
  }

  try {
    await exportModule(moduleName, moduleInputs);
    showSuccessToast(toast, "Export initiated successfully", "top", {
      actionLabel: "Redirect to View Exports",
      actionUrl: "/export"
    });
  } catch (error) {
    handleApiError(error, toast, {
      defaultErrorMessage: `Failed to export ${moduleName}`
    });
  }
};